/**
 * @file ほかの端末と同じページを開く連動で、ほかの端末が開いた本とページへついていくかを決める (純関数)。
 *
 * @what: まなぶ人の閲覧位置の一覧 (新しい順) から、いちばん新しいものを見て、ついていく先
 *   (本とページ) か null を返す。次のときはついていかない。
 *   - 連動していない端末が書いたもの (端末の印が無い)、または自分が書いたもの
 *   - すでに見た時刻より古いか同じもの (自分が最後に送った・ついていった時刻を含む)
 *   - いま開いている本とページと同じもの
 *   - いま開いている本の別冊か、いま開いている別冊の本体 (どちらの向きでも)
 * @why: 別冊を除くのは、PC で解答の別冊を開き、iPad で問題の本に書き込む使い方で、
 *   iPad を別冊へ連れていかないためである (代表の判断、2026-10-10)。本体と別冊はページの
 *   番号も別なので、ページもそろえない。
 * @failure: 時刻が読めないものはついていかない。
 */

import type { StudyReadingPosition } from "../api/readingPositions";
import type { StudySource } from "../api/sources";

export interface DeviceSyncTarget {
  sourceId: string;
  page: number;
  /** 閲覧位置の時刻 (epoch ms)。ついていったら、すでに見た時刻として覚える。 */
  at: number;
}

export function deviceSyncTarget(
  positions: readonly StudyReadingPosition[],
  opts: {
    myDeviceId: string;
    /** すでに見た時刻 (epoch ms)。これ以前の閲覧位置にはついていかない。 */
    seenAt: number;
    /** いま開いている本とページ (本だなにいるときは null)。 */
    current: { sourceId: string; page: number | null } | null;
    sources: readonly StudySource[];
  },
): DeviceSyncTarget | null {
  const newest = newestPosition(positions);
  if (!newest) return null;
  const { position, at } = newest;
  if (!position.device_id || position.device_id === opts.myDeviceId) return null;
  if (at <= opts.seenAt) return null;
  const current = opts.current;
  if (current) {
    if (position.source_id === current.sourceId && position.page === current.page) return null;
    if (
      position.source_id !== current.sourceId &&
      isCompanionPair(opts.sources, position.source_id, current.sourceId)
    ) {
      return null;
    }
  }
  return { sourceId: position.source_id, page: position.page, at };
}

/** 一覧のうち時刻がいちばん新しいもの (サーバは新しい順に返すが、並びには頼らない)。 */
function newestPosition(
  positions: readonly StudyReadingPosition[],
): { position: StudyReadingPosition; at: number } | null {
  let best: { position: StudyReadingPosition; at: number } | null = null;
  for (const position of positions) {
    const at = Date.parse(position.opened_at);
    if (!Number.isFinite(at)) continue;
    if (!best || at > best.at) best = { position, at };
  }
  return best;
}

/** a と b が本体と別冊の組か (どちらが別冊でもよい)。 */
function isCompanionPair(sources: readonly StudySource[], a: string, b: string): boolean {
  const byId = (id: string) => sources.find((s) => s.id === id);
  return byId(a)?.companion_of === b || byId(b)?.companion_of === a;
}
